import { CopyOutlined } from "@ant-design/icons";
import { Button, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { ReactNode } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;

const EndpointBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  margin-bottom: 14px;
`;

const CodeBlock = styled.pre<{ $dark?: boolean }>`
  margin: 0;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  overflow: auto;
  max-height: 320px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas,
    monospace;
  ${(p) =>
    p.$dark
      ? `
    background: #0d1117;
    color: #e6edf3;
    border: 1px solid #30363d;
  `
      : `
    background: #f6f8fa;
    color: #24292f;
    border: 1px solid #d0d7de;
  `}
`;

function SubHeading({ children }: { children: ReactNode }) {
  return (
    <Title level={5} style={{ marginTop: 18, marginBottom: 8, fontSize: 13 }}>
      {children}
    </Title>
  );
}

export type QueryParamRow = {
  key: string;
  name: string;
  type: string;
  defaultVal: string;
  description: string;
};

export type FieldRow = { key: string; field: string; type: string };

export type ApiDocsEndpointSectionProps = {
  /** When true, the GET / title / path bar is omitted (e.g. parent shows it in a collapse header). */
  hideEndpointBar?: boolean;
  path: string;
  title: string;
  intro: string;
  fullUrl: string;
  queryTitle: string;
  queryData: QueryParamRow[];
  fieldsTitle: string;
  fieldRows: FieldRow[];
  responseDesc: string;
  responseJson: string;
  exampleTitle: string;
  curl: string;
};

export function ApiDocsEndpointSection({
  hideEndpointBar = false,
  path,
  title,
  intro,
  fullUrl,
  queryTitle,
  queryData,
  fieldsTitle,
  fieldRows,
  responseDesc,
  responseJson,
  exampleTitle,
  curl,
}: ApiDocsEndpointSectionProps) {
  const { t } = useTranslation();

  const queryParamColumns: ColumnsType<QueryParamRow> = [
    {
      title: t("account.apiDocsTableParam"),
      dataIndex: "name",
      key: "name",
      width: 100,
      render: (name: string) => <Text code>{name}</Text>,
    },
    {
      title: t("account.apiDocsTableType"),
      dataIndex: "type",
      key: "type",
      width: 90,
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t("account.apiDocsTableDefault"),
      dataIndex: "defaultVal",
      key: "defaultVal",
      width: 90,
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: t("account.apiDocsTableDescription"),
      dataIndex: "description",
      key: "description",
    },
  ];

  const fieldColumns: ColumnsType<FieldRow> = [
    {
      title: t("account.apiDocsFieldsColField"),
      dataIndex: "field",
      key: "field",
      width: 160,
      render: (f: string) => <Text code>{f}</Text>,
    },
    {
      title: t("account.apiDocsFieldsColType"),
      dataIndex: "type",
      key: "type",
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
  ];

  const copyCurl = () => {
    void navigator.clipboard.writeText(curl);
    message.success(t("common.copied"));
  };

  return (
    <>
      {!hideEndpointBar ? (
        <EndpointBar>
          <Tag color="success">GET</Tag>
          <Text strong>{title}</Text>
          <Text code style={{ fontSize: 13 }}>
            {path}
          </Text>
        </EndpointBar>
      ) : null}

      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {intro}
      </Paragraph>

      <SubHeading>{t("account.apiDocsRequestUrlTitle")}</SubHeading>
      <CodeBlock>
        <code>GET {fullUrl}</code>
      </CodeBlock>

      <SubHeading>{queryTitle}</SubHeading>
      <Table<QueryParamRow>
        size="small"
        pagination={false}
        rowKey="key"
        columns={queryParamColumns}
        dataSource={queryData}
        style={{ marginBottom: 8 }}
      />

      <SubHeading>{fieldsTitle}</SubHeading>
      <Table<FieldRow>
        size="small"
        pagination={false}
        rowKey="key"
        columns={fieldColumns}
        dataSource={fieldRows}
        style={{ marginBottom: 8 }}
      />

      <SubHeading>{t("account.apiDocsResponseTitle")}</SubHeading>
      <Paragraph style={{ marginBottom: 8 }}>{responseDesc}</Paragraph>
      <CodeBlock $dark>{responseJson}</CodeBlock>

      <SubHeading>{exampleTitle}</SubHeading>
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Button
          type="default"
          size="small"
          icon={<CopyOutlined />}
          onClick={copyCurl}
        >
          {t("account.apiDocsCopyCurl")}
        </Button>
        <CodeBlock $dark>{curl}</CodeBlock>
      </Space>
    </>
  );
}
